import { useState } from "react";
import { type FormikHelpers } from "formik";
import Button from "../UI/Button";
import ButtonCard from "../UI/ButtonCard";
import Card from "../UI/Card";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Status from "../UI/Status";
import ModalDialog from "../UI/ModalDialog";
import EdlItemForm, { type EdlItemValues } from "./EdlItemForm";
// @ts-expect-error pure js
import EdlMateriauxTemplateForm from "./EdlMateriauxTemplateForm";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import useEdlItemsTemplates from "../../hooks/use-edl-items-templates";
// @ts-expect-error pure js
import useEdlMateriauxTemplates from "../../hooks/use-edl-materiaux-templates";
import styles from "./EdlTemplateShow.module.css";

interface EdlMateriauxTemplate {
  id: number;
  nom: string;
  materiaux: string[];
  degradations: string[];
}

interface EdlMateriauxTemplateValues {
  nom: string;
  materiaux: string[];
  degradations: string[];
}

const ITEM_SECTIONS = [
  { type: "edl", label: "Éléments" },
  { type: "equipements", label: "Équipements" },
  { type: "meubles", label: "Mobilier" },
] as const;

export default function EdlTemplateShow() {
  const [isPieceOpen, setIsPieceOpen] = useState(false);
  const [materiauxOpen, setMateriauxOpen] = useState(false);
  const [materiauxTemplateToEdit, setMateriauxTemplateToEdit] =
    useState<EdlMateriauxTemplate | null>(null);
  const [itemToEdit, setItemToEdit] = useState<EdlItemValues | null>(null);
  const {
    edlItemsTemplates,
    addEdlItemTemplate,
    updateEdlItemTemplate,
    deleteEdlItemTemplate,
  } = useEdlItemsTemplates() as {
    edlItemsTemplates: EdlItemValues[];
    addEdlItemTemplate: (values: unknown) => Promise<unknown>;
    updateEdlItemTemplate: (
      id: number | undefined,
      values: unknown,
    ) => Promise<unknown>;
    deleteEdlItemTemplate: (id: number) => Promise<unknown>;
  };
  const {
    edlMateriauxTemplates,
    addEdlItemMateriaux,
    updateEdlItemMateriaux,
    deleteEdlItemMateriaux,
  } = useEdlMateriauxTemplates() as {
    edlMateriauxTemplates: EdlMateriauxTemplate[];
    addEdlItemMateriaux: (values: unknown) => Promise<unknown>;
    updateEdlItemMateriaux: (id: number, values: unknown) => Promise<unknown>;
    deleteEdlItemMateriaux: (id: number) => Promise<unknown>;
  };

  async function submitItemHandler(
    values: EdlItemValues,
    { resetForm }: FormikHelpers<EdlItemValues>,
  ) {
    const sanitizedValues = { ...sanitizeValues(values), type: "edl" };
    delete sanitizedValues.template;
    delete sanitizedValues.commentaire;

    try {
      if (itemToEdit) {
        await updateEdlItemTemplate(itemToEdit.id, sanitizedValues);
      } else {
        await addEdlItemTemplate(sanitizedValues);
      }
      resetForm();
      setIsPieceOpen(false);
    } catch (error) {
      console.error(error);
    }
  }

  async function submitMateriauxTemplateHandler(
    values: EdlMateriauxTemplateValues,
    { resetForm }: FormikHelpers<EdlMateriauxTemplateValues>,
  ) {
    const sanitizedValues = sanitizeValues(values);
    try {
      if (materiauxTemplateToEdit) {
        await updateEdlItemMateriaux(
          materiauxTemplateToEdit.id,
          sanitizedValues,
        );
      } else {
        await addEdlItemMateriaux(sanitizedValues);
      }
      resetForm();
      setMateriauxOpen(false);
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteHandler(id: number) {
    try {
      await deleteEdlItemTemplate(id);
      setIsPieceOpen(false);
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteMateriauxHandler(id: number) {
    try {
      await deleteEdlItemMateriaux(id);
      setMateriauxOpen(false);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <>
      <EdlItemForm
        open={isPieceOpen}
        onOpenChange={setIsPieceOpen}
        itemToEdit={itemToEdit}
        onSubmit={submitItemHandler}
        onDelete={deleteHandler}
        template
      />
      <ModalDialog
        open={materiauxOpen}
        header={
          materiauxTemplateToEdit
            ? "Modifier un type de surface"
            : "Ajouter un type de surface"
        }
        onOpenChange={(open) => {
          setMateriauxOpen(open);
          if (!open) setMateriauxTemplateToEdit(null);
        }}
      >
        <EdlMateriauxTemplateForm
          materiauxTemplateToEdit={materiauxTemplateToEdit}
          onSubmit={submitMateriauxTemplateHandler}
          onDelete={deleteMateriauxHandler}
        />
      </ModalDialog>

      <div className={styles.cards}>
        <Card>
          <div className={styles.cardHeader}>
            <Title variant="kilo">Template de pièce</Title>
            <Button
              type="button"
              onClick={() => {
                setItemToEdit(null);
                setIsPieceOpen(true);
              }}
            >
              Ajouter une pièce
            </Button>
          </div>
          {edlItemsTemplates?.map((item) => (
            <ButtonCard
              key={item.id}
              onClick={() => {
                setItemToEdit(item);
                setIsPieceOpen(true);
              }}
              className={styles.templateButton}
            >
              {item.is_shared && (
                <Status
                  color="success"
                  className={styles.sharedBadge}
                  text="Pièce partagée"
                />
              )}
              <Title variant="hecto" weight="bold" as="h3">
                {item.nom}
              </Title>
              {ITEM_SECTIONS.map((section) => (
                <div key={section.type}>
                  <Text weight="bold">{section.label}</Text>
                  {item.items
                    .filter((child) => child.type === section.type)
                    .map((child) => (
                      <Text
                        key={child.id ?? child.nom}
                        variant="deci"
                        color="muted"
                      >
                        {child.nom}
                      </Text>
                    ))}
                </div>
              ))}
            </ButtonCard>
          ))}
        </Card>

        <Card>
          <div className={styles.cardHeader}>
            <Title variant="kilo">Template de type de surface</Title>
            <Button
              type="button"
              onClick={() => {
                setMateriauxTemplateToEdit(null);
                setMateriauxOpen(true);
              }}
            >
              Ajouter un type de surface
            </Button>
          </div>
          {edlMateriauxTemplates?.map((item) => (
            <ButtonCard
              key={item.id}
              onClick={() => {
                setMateriauxTemplateToEdit(item);
                setMateriauxOpen(true);
              }}
              className={styles.templateButton}
            >
              <Title variant="hecto" weight="bold" as="h3">
                {item.nom}
              </Title>
              <div>
                <Text weight="bold">Matériaux</Text>
                {item.materiaux.map((elem) => (
                  <Text key={elem} variant="deci" color="muted">
                    {elem}
                  </Text>
                ))}
              </div>
              <div>
                <Text weight="bold">Dégradations</Text>
                {item.degradations.map((degradation) => (
                  <Text key={degradation} variant="deci" color="muted">
                    {degradation}
                  </Text>
                ))}
              </div>
            </ButtonCard>
          ))}
        </Card>
      </div>
    </>
  );
}
